import React, { useState } from "react";
import {
  Box,
  Button,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { toast } from "react-toastify";

import { NURSE_COLORS } from "../../../nurse-dashboard/theme";
import { convertEmergencyToIpd } from "../../api/emergencyToIpdApi";

const PAYER_TYPES = ["self_pay", "insurance", "corporate", "government"];

const EMPTY_FORM = {
  patientId: "",
  emergencyVisitId: "",
  admittingDoctorId: "",
  assignedDoctorId: "",
  admissionType: "Immediate",
  admissionDetails: "",
  payerType: "",
  governmentScheme: "",
  corporateName: "",
  corporateReference: "",
  insuranceProvider: "",
  insurancePolicyNo: "",
  tpaName: "",
};

// Standalone conversion form: Emergency -> IPD (G11 Part A). Converts an
// already-registered patient's emergency/OPD visit into an IPD admission
// (no bed allocated yet — that happens via the normal Bed Allocation page
// afterwards). Not linked from src/pages/emergency/* — that module is
// currently mock-only with no real visit records to convert from.
export default function EmergencyToIpdForm() {
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const setField = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleSubmit = async () => {
    if (!form.patientId.trim() || !form.admittingDoctorId.trim()) {
      toast.error("Patient ID and Admitting Doctor ID are required.");
      return;
    }
    if (form.payerType === "government" && !form.governmentScheme.trim()) {
      toast.error("Government scheme is required for payer type 'government'.");
      return;
    }
    if (form.payerType === "corporate" && !form.corporateName.trim()) {
      toast.error("Corporate name is required for payer type 'corporate'.");
      return;
    }

    setSubmitting(true);
    setResult(null);
    try {
      const data = await convertEmergencyToIpd({
        patientId: form.patientId.trim(),
        emergencyVisitId: form.emergencyVisitId.trim() || undefined,
        admittingDoctorId: form.admittingDoctorId.trim(),
        assignedDoctorId: form.assignedDoctorId.trim() || undefined,
        admissionType: form.admissionType || undefined,
        admissionDetails: form.admissionDetails.trim() || undefined,
        payerType: form.payerType || undefined,
        governmentScheme: form.governmentScheme.trim() || undefined,
        corporateName: form.corporateName.trim() || undefined,
        corporateReference: form.corporateReference.trim() || undefined,
        insuranceProvider: form.insuranceProvider.trim() || undefined,
        insurancePolicyNo: form.insurancePolicyNo.trim() || undefined,
        tpaName: form.tpaName.trim() || undefined,
      });
      if (data?.status === "Success") {
        toast.success(data.message || "Converted to IPD admission.");
        setResult(data.data || null);
        setForm(EMPTY_FORM);
      } else {
        toast.error(data?.message || "Could not convert to an IPD admission.");
      }
    } catch (err) {
      console.error("Emergency to IPD conversion failed:", err);
      toast.error(err?.response?.data?.message || "Could not convert to an IPD admission.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ bgcolor: NURSE_COLORS.pageBg, minHeight: "100vh", p: 3 }}>
      <Typography variant="h6" color={NURSE_COLORS.textPrimary} sx={{ mb: 2 }}>
        Emergency → IPD Conversion
      </Typography>

      <Paper sx={{ p: 3, borderRadius: 2, border: `1px solid ${NURSE_COLORS.border}`, maxWidth: 720 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              label="Patient ID"
              required
              value={form.patientId}
              onChange={setField("patientId")}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              label="Emergency Visit ID (optional)"
              value={form.emergencyVisitId}
              onChange={setField("emergencyVisitId")}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              label="Admitting Doctor ID"
              required
              value={form.admittingDoctorId}
              onChange={setField("admittingDoctorId")}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              label="Assigned Doctor ID (optional)"
              value={form.assignedDoctorId}
              onChange={setField("assignedDoctorId")}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Admission Type</InputLabel>
              <Select label="Admission Type" value={form.admissionType} onChange={setField("admissionType")}>
                <MenuItem value="Immediate">Immediate</MenuItem>
                <MenuItem value="Planned">Planned</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Payer Type (optional)</InputLabel>
              <Select label="Payer Type (optional)" value={form.payerType} onChange={setField("payerType")}>
                <MenuItem value="">None</MenuItem>
                {PAYER_TYPES.map((p) => (
                  <MenuItem key={p} value={p}>
                    {p}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {form.payerType === "government" && (
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Government Scheme"
                required
                value={form.governmentScheme}
                onChange={setField("governmentScheme")}
              />
            </Grid>
          )}
          {form.payerType === "corporate" && (
            <>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Corporate Name"
                  required
                  value={form.corporateName}
                  onChange={setField("corporateName")}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Corporate Reference (optional)"
                  value={form.corporateReference}
                  onChange={setField("corporateReference")}
                />
              </Grid>
            </>
          )}
          {form.payerType === "insurance" && (
            <>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Insurance Provider (optional)"
                  value={form.insuranceProvider}
                  onChange={setField("insuranceProvider")}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Insurance Policy No. (optional)"
                  value={form.insurancePolicyNo}
                  onChange={setField("insurancePolicyNo")}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="TPA Name (optional)"
                  value={form.tpaName}
                  onChange={setField("tpaName")}
                />
              </Grid>
            </>
          )}

          <Grid item xs={12}>
            <TextField
              fullWidth
              size="small"
              multiline
              minRows={2}
              label="Admission Details / Notes (optional)"
              value={form.admissionDetails}
              onChange={setField("admissionDetails")}
            />
          </Grid>

          <Grid item xs={12} sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Button variant="contained" onClick={handleSubmit} disabled={submitting}>
              {submitting ? "Converting..." : "Convert to IPD Admission"}
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {result && (
        <Paper sx={{ p: 2, mt: 2, borderRadius: 2, border: `1px solid ${NURSE_COLORS.border}`, maxWidth: 720 }}>
          <Typography sx={{ fontWeight: 600, mb: 1 }}>Admission Created</Typography>
          <Typography sx={{ fontSize: "13px" }}>Admission ID: {result.admission_id}</Typography>
          <Typography sx={{ fontSize: "13px" }}>Status: {result.status}</Typography>
          <Typography sx={{ fontSize: "13px" }}>
            Next step: allocate a bed from the Bed Allocation page.
          </Typography>
        </Paper>
      )}
    </Box>
  );
}
